import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import { RestartAlt, Save, Search } from '@mui/icons-material'
import { Alert, Box, Button, Card, Chip, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material'

import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog'
import { GameIcon } from '../../../../../features/game'
import { ConfirmDialogData } from '../../../../../types/ConfirmDialogData'
import { useGetInternalGameQuery } from '../../../../api/internal-games'
import { useGetMotivationAnalysisQuery, useOverrideMotivationAnalysisMutation } from '../../../../api/motivations'
import { DiscardChangesDialog } from '../../../../components/DiscardChangesDialog/DiscardChangesDialog'
import { InternalGameSelectorDialog } from '../../../../components/InternalGameSelectorDialog/InternalGameSelectorDialog'
import { useEditPageLeave } from '../../../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../../../hooks/useMutationFeedback'
import {
  AttributeFormValue,
  AttributeValueError,
  getAttributeValueError,
  getEditableAttributes,
  getUnstoredAttributes,
  hasMotivationOverrides,
  maxAttributeValue,
  minAttributeValue,
  toFinalAttributes,
} from '../../helpers/motivationOverrideHelpers'
import { MotivationAttributes } from '../../types/Motivations'

type FormValues = { attributes: AttributeFormValue[] }

type OverrideAction = { action: 'save' | 'reset'; finalAttributes: MotivationAttributes }

const valueErrorKeys: Record<AttributeValueError, string> = {
  required: 'internal-service-management:motivations_value_required',
  invalid: 'internal-service-management:motivations_value_invalid',
  range: 'internal-service-management:motivations_value_range',
}

export const MotivationOverridesTab: FC = () => {
  const { t } = useTranslation()
  const { run, notifySuccess, notifyError } = useMutationFeedback()
  const [searchParams, setSearchParams] = useSearchParams()
  const gameId = searchParams.get('gameId') ?? undefined
  const [selectorOpen, setSelectorOpen] = useState(false)
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<OverrideAction>>()
  const [pendingGameId, setPendingGameId] = useState<string>()

  const gameQuery = useGetInternalGameQuery(gameId ?? '', { skip: !gameId })
  const game = gameId ? gameQuery.data : undefined
  const analysisQuery = useGetMotivationAnalysisQuery(game?.appId ?? 0, { skip: !game?.appId })
  const analysis = game?.appId ? analysisQuery.data : undefined
  const [overrideAnalysis, { isLoading: isSaving }] = useOverrideMotivationAnalysisMutation()

  const attributes = useMemo(() => getEditableAttributes(analysis), [analysis])
  const hasOverrides = hasMotivationOverrides(analysis)
  const { control, handleSubmit, reset, formState } = useForm<FormValues>({ mode: 'onChange', defaultValues: { attributes } })
  // The motivation tabs are separate routes, so this also asks before another tab replaces this one.
  const { guard } = useEditPageLeave(formState.isDirty)

  useEffect(() => {
    reset({ attributes })
  }, [attributes, reset])

  const selectGame = useCallback(
    (games: { id: string }[]) => {
      setSelectorOpen(false)
      const nextGameId = games[0]?.id
      if (!nextGameId || nextGameId === gameId) return
      if (formState.isDirty) setPendingGameId(nextGameId)
      else setSearchParams({ gameId: nextGameId })
    },
    [formState.isDirty, gameId, setSearchParams]
  )

  const handleDiscardClose = (discard: boolean) => {
    if (pendingGameId) {
      if (discard) setSearchParams({ gameId: pendingGameId })
      setPendingGameId(undefined)
    } else if (discard) guard.proceed()
    else guard.cancel()
  }

  const askToSave = handleSubmit((values) =>
    setConfirmDialogData({
      title: t('internal-service-management:motivations_save_overrides'),
      confirmText: (
        <Stack spacing={2}>
          <Typography>{t('internal-service-management:motivations_save_overrides_confirm', { name: game?.name })}</Typography>
          <Alert severity="info">{t('internal-service-management:motivations_segments_recalculated')}</Alert>
        </Stack>
      ),
      actionText: t('internal-common:save'),
      data: { action: 'save', finalAttributes: toFinalAttributes(values.attributes) },
    })
  )

  const askToReset = () =>
    setConfirmDialogData({
      title: t('internal-service-management:motivations_reset'),
      confirmText: (
        <Stack spacing={2}>
          <Typography>{t('internal-service-management:motivations_reset_confirm', { name: game?.name })}</Typography>
          <Alert severity="info">{t('internal-service-management:motivations_segments_recalculated')}</Alert>
        </Stack>
      ),
      actionText: t('internal-service-management:motivations_reset'),
      destructiveAction: true,
      data: { action: 'reset', finalAttributes: { ...(analysis?.correctedAttributes ?? {}) } },
    })

  const handleConfirm = async (confirmed?: ConfirmDialogData<OverrideAction>) => {
    setConfirmDialogData(undefined)
    const data = confirmed?.data
    if (!data || !game?.appId) return
    const messageKey = data.action === 'save' ? 'motivations_overrides_saved' : 'motivations_overrides_reset'
    const saved = await run(overrideAnalysis({ appId: game.appId, finalAttributes: data.finalAttributes }).unwrap(), {
      error: t('internal-service-management:motivations_overrides_failed'),
    })
    if (!saved.ok) return
    const unstored = getUnstoredAttributes(data.finalAttributes, saved.result)
    if (unstored.length) {
      notifyError(t('internal-service-management:motivations_overrides_not_stored', { fields: unstored.join(', ') }))
      return
    }
    notifySuccess(t(`internal-service-management:${messageKey}`, { name: game.name }))
  }

  const isLoading = (!!gameId && gameQuery.isFetching && !game) || (!!game?.appId && analysisQuery.isLoading)
  const canEdit = !!game && !!analysis

  return (
    <Box>
      <Stack direction="row" spacing={1} mb={2} justifyContent="flex-end">
        <Button variant="outlined" startIcon={<Search />} onClick={() => setSelectorOpen(true)}>
          {t('internal-service-management:motivations_select_game')}
        </Button>
        {canEdit && (
          <>
            <Button variant="outlined" color="warning" startIcon={<RestartAlt />} onClick={askToReset} disabled={!hasOverrides || isSaving}>
              {t('internal-service-management:motivations_reset')}
            </Button>
            <Button variant="contained" startIcon={<Save />} onClick={askToSave} disabled={!formState.isDirty || !formState.isValid || isSaving}>
              {t('internal-common:save')}
            </Button>
          </>
        )}
      </Stack>

      {!gameId ? (
        <Typography color="text.secondary">{t('internal-service-management:motivations_no_game')}</Typography>
      ) : isLoading ? (
        <Skeleton variant="rectangular" height={480} />
      ) : gameQuery.isError || !game ? (
        <Typography color="error.main">{t('internal-service-management:motivations_game_load_failed')}</Typography>
      ) : (
        <Stack spacing={2}>
          <Card sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <GameIcon src={game.getIcon() as string} gameName={game.name ?? ''} size="small" />
              <Box flexGrow={1}>
                <Typography fontWeight="bold">{game.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {game.artist}
                </Typography>
              </Box>
              {analysis &&
                (hasOverrides ? (
                  <Chip color="warning" label={t('internal-service-management:motivations_overridden_values')} />
                ) : (
                  <Chip variant="outlined" label={t('internal-service-management:motivations_original_values')} />
                ))}
            </Stack>
          </Card>

          {analysisQuery.isError ? (
            <Typography color="error.main">{t('internal-service-management:motivations_analysis_load_failed')}</Typography>
          ) : !analysis ? (
            <Typography color="text.secondary">{t('internal-service-management:motivations_no_analysis')}</Typography>
          ) : (
            <Card>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '30%' }}>{t('internal-service-management:motivations_attribute')}</TableCell>
                    <TableCell>{t('internal-service-management:motivations_value')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attributes.map((attribute, index) => (
                    <TableRow key={attribute.name}>
                      <TableCell>{attribute.name}</TableCell>
                      <TableCell>
                        <Controller
                          name={`attributes.${index}.value`}
                          control={control}
                          rules={{
                            validate: (value) => {
                              const error = getAttributeValueError(value)
                              return error ? t(valueErrorKeys[error], { min: minAttributeValue, max: maxAttributeValue }) : true
                            },
                          }}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              value={field.value ?? ''}
                              type="number"
                              size="small"
                              inputProps={{ min: minAttributeValue, max: maxAttributeValue, step: 'any', 'aria-label': attribute.name }}
                              error={!!fieldState.error}
                              helperText={fieldState.error?.message}
                              sx={{ maxWidth: 200 }}
                            />
                          )}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          )}
        </Stack>
      )}

      <InternalGameSelectorDialog
        open={selectorOpen}
        title={t('internal-service-management:motivations_select_game')}
        maxSelected={1}
        allowAllGames
        onClose={() => setSelectorOpen(false)}
        onDone={selectGame}
      />
      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
      <DiscardChangesDialog
        guard={guard}
        open={guard.isBlocked || !!pendingGameId}
        title={t('internal-service-management:motivations_discard_title')}
        text={t('internal-service-management:motivations_discard_confirm', { name: game?.name })}
        discardText={t('internal-service-management:motivations_discard')}
        onClose={handleDiscardClose}
      />
    </Box>
  )
}
